import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import { getData } from "features/dinamic";

interface DashboardState {
  status: string;
  error: string;
  kpis: {
    totalPorCalificar: number;
    actividadesVencidas: number;
    actividadesSemana: number;
  } | null;
  proximas: any[];
  vencidas: any[];
  actividades: any[];
  entregasPendientes: any[];
  tareasActivas: any[];
  tareasFaltanEntregas: any[];
  entregasPendientesTotal: number;
  tareasActivasTotal: number;
  tareasFaltanEntregasTotal: number;
  entregasPendientesPage: number;
  tareasActivasPage: number;
  tareasFaltanEntregasPage: number;
}

const initialState: DashboardState = {
  status: 'idle',
  error: '',
  kpis: null,
  proximas: [],
  vencidas: [],
  actividades: [],
  entregasPendientes: [],
  tareasActivas: [],
  tareasFaltanEntregas: [],
  entregasPendientesTotal: 0,
  tareasActivasTotal: 0,
  tareasFaltanEntregasTotal: 0,
  entregasPendientesPage: 1,
  tareasActivasPage: 1,
  tareasFaltanEntregasPage: 1,
}

export const fetchDashboardFacilitador = createAsyncThunk('facilitador/fetchDashboard', async (_: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 133;
  const { result, message, data } = await getData({ idsp }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchActividadesGlobal = createAsyncThunk('facilitador/fetchActividadesGlobal', async ({ idCiclo, idModulo, idSesion, idGrupo, page = 1, pageSize = 10 }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 134;
  const { result, message, data } = await getData({ idsp, idCiclo, idModulo, idSesion, idGrupo, page, pageSize }, getState, dispatch);
  if (result) {
    return { data };
  }
  return rejectWithValue(message);
});

export const fetchEntregasPendientes = createAsyncThunk('facilitador/fetchEntregasPendientes', async ({ idCiclo, idModulo, idSesion, idGrupo, idTareaSesion, estado = 'pendiente', page = 1, pageSize = 5, append = false }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 135;
  const params: any = { idsp, estado, page, pageSize };
  if (idCiclo) params.idCiclo = idCiclo;
  if (idModulo) params.idModulo = idModulo;
  if (idSesion) params.idSesion = idSesion;
  if (idGrupo) params.idGrupo = idGrupo;
  if (idTareaSesion) params.idTareaSesion = idTareaSesion;

  const { result, message, data } = await getData(params, getState, dispatch);
  if (result) {
    return { data, append };
  }
  return rejectWithValue(message);
});

export const fetchTareasActivas = createAsyncThunk('facilitador/fetchTareasActivas', async ({ idCiclo, idModulo, idSesion, idGrupo, page = 1, pageSize = 5, append = false }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 134;
  const params: any = { idsp, page, pageSize };
  if (idCiclo) params.idCiclo = idCiclo;
  if (idModulo) params.idModulo = idModulo;
  if (idSesion) params.idSesion = idSesion;
  if (idGrupo) params.idGrupo = idGrupo;

  const { result, message, data } = await getData(params, getState, dispatch);
  if (result) {
    return { data, append };
  }
  return rejectWithValue(message);
});

export const fetchTareasFaltanEntregas = createAsyncThunk('facilitador/fetchTareasFaltanEntregas', async ({ idCiclo, idModulo, idSesion, idGrupo, page = 1, pageSize = 5, append = false }: any, { dispatch, getState, rejectWithValue }) => {
  const idsp = 134;
  const params: any = { idsp, page, pageSize };
  if (idCiclo) params.idCiclo = idCiclo;
  if (idModulo) params.idModulo = idModulo;
  if (idSesion) params.idSesion = idSesion;
  if (idGrupo) params.idGrupo = idGrupo;

  const { result, message, data } = await getData(params, getState, dispatch);
  if (result) {
    const tareasFaltantes = Array.isArray(data)
      ? data.filter((tarea: any) => (tarea.totalSinEntregar && tarea.totalSinEntregar > 0) || (tarea.totalAsignados && tarea.totalEntregadas && tarea.totalAsignados > tarea.totalEntregadas))
      : (data?.items || []).filter((tarea: any) => (tarea.totalSinEntregar && tarea.totalSinEntregar > 0) || (tarea.totalAsignados && tarea.totalEntregadas && tarea.totalAsignados > tarea.totalEntregadas));
    return { data: tareasFaltantes, total: data?.total, append };
  }
  return rejectWithValue(message);
});

const dashboardSlice = createSlice({
  name: 'facilitadorDashboard',
  initialState,
  reducers: {
    clearFacilitadorDashboard: () => initialState,
    invalidateDashboardCache: (state) => {
      state.status = 'idle';
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchDashboardFacilitador.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchDashboardFacilitador.fulfilled, (state, action) => {
        const data = action.payload?.data || {};
        state.status = 'completed';
        state.kpis = {
          totalPorCalificar: data.totalPorCalificar || 0,
          actividadesVencidas: data.actividadesVencidas || 0,
          actividadesSemana: data.actividadesSemana || 0,
        } as any;
        state.proximas = data.proximasVencimientos || [];
        state.vencidas = data.vencidas || [];
      })
      .addCase(fetchDashboardFacilitador.rejected, (state, action) => { state.status = 'failed'; state.error = action.payload as string; })
      .addCase(fetchActividadesGlobal.fulfilled, (state, action) => {
        const data = action.payload?.data;
        if (Array.isArray(data)) {
          state.actividades = data;
        } else if (data?.items) {
          state.actividades = data.items;
        } else {
          state.actividades = [];
        }
      })
      .addCase(fetchEntregasPendientes.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const append = action.payload?.append;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data?.items) {
          items = data.items;
          state.entregasPendientesTotal = data.total || 0;
        }

        if (append) {
          state.entregasPendientes = [...state.entregasPendientes, ...items];
          state.entregasPendientesPage += 1;
        } else {
          state.entregasPendientes = items;
          state.entregasPendientesPage = 1;
          state.entregasPendientesTotal = data?.total || items.length;
        }
      })
      .addCase(fetchTareasActivas.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const append = action.payload?.append;
        let items = [];
        if (Array.isArray(data)) {
          items = data;
        } else if (data?.items) {
          items = data.items;
          state.tareasActivasTotal = data.total || 0;
        }

        if (append) {
          state.tareasActivas = [...state.tareasActivas, ...items];
          state.tareasActivasPage += 1;
        } else {
          state.tareasActivas = items;
          state.tareasActivasPage = 1;
          state.tareasActivasTotal = data?.total || items.length;
        }
      })
      .addCase(fetchTareasFaltanEntregas.fulfilled, (state, action) => {
        const data = action.payload?.data;
        const append = action.payload?.append;
        const total = action.payload?.total;
        const items = Array.isArray(data) ? data : [];

        if (append) {
          state.tareasFaltanEntregas = [...state.tareasFaltanEntregas, ...items];
          state.tareasFaltanEntregasPage += 1;
        } else {
          state.tareasFaltanEntregas = items;
          state.tareasFaltanEntregasPage = 1;
          state.tareasFaltanEntregasTotal = total || items.length;
        }
      })
  }
});

export const selectFacDashboardKpis = (state: RootState) => state.facilitadorDashboard.kpis;
export const selectFacDashboardProximas = (state: RootState) => state.facilitadorDashboard.proximas;
export const selectFacDashboardVencidas = (state: RootState) => state.facilitadorDashboard.vencidas;
export const selectFacDashboardActividades = (state: RootState) => state.facilitadorDashboard.actividades;
export const selectFacDashboardEntregasPendientes = (state: RootState) => state.facilitadorDashboard.entregasPendientes;
export const selectFacDashboardTareasActivas = (state: RootState) => state.facilitadorDashboard.tareasActivas;
export const selectFacDashboardTareasFaltanEntregas = (state: RootState) => state.facilitadorDashboard.tareasFaltanEntregas;
export const selectFacDashboardEntregasPendientesTotal = (state: RootState) => state.facilitadorDashboard.entregasPendientesTotal;
export const selectFacDashboardTareasActivasTotal = (state: RootState) => state.facilitadorDashboard.tareasActivasTotal;
export const selectFacDashboardTareasFaltanEntregasTotal = (state: RootState) => state.facilitadorDashboard.tareasFaltanEntregasTotal;
export const selectFacDashboardEntregasPendientesPage = (state: RootState) => state.facilitadorDashboard.entregasPendientesPage;
export const selectFacDashboardTareasActivasPage = (state: RootState) => state.facilitadorDashboard.tareasActivasPage;
export const selectFacDashboardTareasFaltanEntregasPage = (state: RootState) => state.facilitadorDashboard.tareasFaltanEntregasPage;

export const { clearFacilitadorDashboard, invalidateDashboardCache } = dashboardSlice.actions;

export default dashboardSlice.reducer;

